import { AxiosInstance, AxiosRequestConfig } from 'axios'
import { axiosProxy } from '@/plugins/axios'
import { Artwork } from './Artwork'
import { ArtworkTransformer } from './ArtworkTransformer'
import { PaginationTransformer } from '../pagination/PaginationTransformer'
import { ProfileArtworkTransformer } from './ProfileArtworkTransformer'
import { ArtworkReaction } from '@/services/artwork/ArtworkReaction'
import { objectToFormData } from '@/utils/FormHelper'

export class ArtworkRepository {
  private api: AxiosInstance = axiosProxy

  // Get single artwork with slug
  async getArtwork (slug: string) {
    try {
      const res = await this.api.get(`artworks/${slug}?include=media,artist,artist.media,category,technique,themes`)
      if (res.data['data']) {
        return new ArtworkTransformer().toClass(res.data['data'])
      }
      throw Error('Data not in respose.')
    } catch (error) {
      throw error
    }
  }

  // Get sold artworks, uses at home.vue
  async soldArtworks () {
    try {
      const res = await this.api.get(`home/sold-artworks`)
      if (res.data['data']) {
        return res.data['data'].map((artwork: Artwork) => new ArtworkTransformer().toClass(artwork))
      }
      throw Error('Data not in respose.')
    } catch (error) {
      throw error
    }
  }

  // Get latest artworks, uses at home.vue
  async latestArtworks () {
    try {
      const res = await this.api.get(`home/latest-artworks`)
      if (res.data['data']) {
        return res.data['data'].map((artwork: Artwork) => new ArtworkTransformer().toClass(artwork))
      }
      throw Error('Data not in respose.')
    } catch (error) {
      throw error
    }
  }

  // Get most viewed artworks, uses at home.vue
  async mostViewedArtworks () {
    try {
      const res = await this.api.get(`home/most-viewed-artworks`)
      if (res.data['data']) {
        return res.data['data'].map((artwork: Artwork) => new ArtworkTransformer().toClass(artwork))
      }
      throw Error('Data not in respose.')
    } catch (error) {
      throw error
    }
  }

  // Get sold artworks, uses at home.vue
  async artworks (query: Object = {}) {
    try {
      const res = await this.api.get(`artworks`, { params: query })
      if (res.data['data']) {
        return res.data['data'].map((artwork: Artwork) => new ArtworkTransformer().toClass(artwork))
      }
      throw Error('Data not in respose.')
    } catch (error) {
      throw error
    }
  }

  /**********************************************************
   ***** Functiosn realated profile artwork management ******
   **********************************************************/
  // Returns artist's published artworks
  async profilePublishedArtworks (page: number = 1) {
    try {
      const res = await this.api.get(
        `profile/artworks?sort=order&page=${page}&filter[published]=true&filter[sold]=false`)
      return {
        data: res.data['data'].map((artwork: Artwork) => new ProfileArtworkTransformer().toClass(artwork)),
        pagination: new PaginationTransformer().toClass(res.data['meta'])
      }
    } catch (error) {
      throw error
    }
  }

  // Returns artist's unfinished artworks
  async profileDraftArtworks (page: number = 1) {
    try {
      const res = await this.api.get(`profile/artworks?page=${page}&filter[published]=false`)
      return {
        data: res.data['data'].map((artwork: Artwork) => {
          // Modify path so that its path is edit page
          artwork.path = `/profile/artworks/edit/${artwork.id}`
          return new ProfileArtworkTransformer().toClass(artwork)
        }),
        pagination: new PaginationTransformer().toClass(res.data['meta'])
      }
    } catch (error) {
      throw error
    }
  }

  // Returns artist's sold artworks
  async profileSoldArtworks (page: number = 1) {
    try {
      const res = await this.api.get(`profile/sales?page=${page}`)
      return {
        data: res.data['data'].map((artwork: Artwork) => new ProfileArtworkTransformer().toClass(artwork)),
        pagination: new PaginationTransformer().toClass(res.data['meta'])
      }
    } catch (error) {
      throw error
    }
  }

  // Returns users favorites
  async profileFavorites (page: number = 1) {
    try {
      const res = await this.api.get(`profile/favorites?page=${page}`)
      return {
        data: res.data['data'].map((artwork: Artwork) => new ProfileArtworkTransformer().toClass(artwork)),
        pagination: new PaginationTransformer().toClass(res.data['meta'])
      }
    } catch (error) {
      throw error
    }
  }

  // Retursn users purchases
  async profilePurchasedArtworks (page: number = 1) {
    try {
      const res = await this.api.get(`profile/purchases?page=${page}`)
      return {
        data: res.data['data'].map((artwork: Artwork) => new ProfileArtworkTransformer().toClass(artwork)),
        pagination: new PaginationTransformer().toClass(res.data['meta'])
      }
    } catch (error) {
      throw error
    }
  }

  // Return all artworks for sorting purpose
  async profileAllArtworks () {
    try {
      const res = await this.api.get(`profile/artworks?sort=order&limit=500&filter[published]=true`)
      return res.data['data'].map((artwork: Artwork) => new ProfileArtworkTransformer().toClass(artwork))
    } catch (error) {
      throw error
    }
  }

  // Saves artwork sorting
  async sortArtworks (artworkIds: string[] = []) {
    try {
      return await this.api.put(`profile/artworks/order`, { artworks: artworkIds })
    } catch (error) {
      throw error
    }
  }

  // Get artwork for editing purpose
  async profileArtwork (id: string) {
    try {
      const res = await this.api.get(`profile/artworks/${id}?include=media,category,themes,technique,keywords`)
      if (res.data['data']) {
        return new ProfileArtworkTransformer().toClass(res.data['data'])
      }
      throw Error('Data not in respose.')
    } catch (error) {
      throw error
    }
  }

  /**
   * Create or update artwork
   *
   * @param artworkId The artwork id to be updated. Will be null in step 1 when a new artwork is being created.
   * @param step This indicates the current step from where the request is being made (affects to validation rules).
   * @param saveAndReturn This indicates how the data validation should be performed (when true fields can be left empty).
   * @param payload The data of artwork to be saved.
   * @param config Additional Axios configuration.
   */
  async createArtwork (artworkId: string | null, step: number = 1, saveAndReturn: boolean = false, payload: any, config: AxiosRequestConfig = {}) {
    try {
      let url

      const reqType = artworkId ? 'put' : 'post'
      const includes = 'media,category,themes,technique'

      if (reqType === 'put') {
        if (saveAndReturn) {
          url = `profile/artworks/${artworkId}?r=1&step=${step}&include=${includes}`
        }
        else {
          url = `profile/artworks/${artworkId}?step=${step}&include=${includes}`
        }
      }
      else {
        url = `profile/artworks?include=${includes}`
      }

      const res = await this.api[reqType](url, payload, config)

      return new ProfileArtworkTransformer().toClass(res.data['data'])
    } catch (error) {
      throw error
    }
  }

  /**
   * Upload artwork video
   * @param artworkId
   * @param payload Form data
   */
  async uploadVideo (artworkId: string, payload: any, config: AxiosRequestConfig = {}) {
    try {
      return await this.api.post(`profile/artworks/${artworkId}/video`, objectToFormData(payload), config)
    } catch (error) {
      throw error
    }
  }

  /**
   * Deletes video
   * @param artworkId
   * @returns
   */
  async deleteVideo (artworkId: string) {
    try {
      return await this.api.delete(`profile/artworks/${artworkId}/video`)
    } catch (error) {
      throw error
    }
  }

  /**
   * Gets video meta like cover image
   * @param artworkId
   */
  async videoMeta (artworkId: string, videoId: number, isProfile: boolean) {
    try {
      const prefex = isProfile ? 'profile/' : ''
      const url = `${prefex}artworks/${artworkId}/video/${videoId}`
      return this.api.get(url)
    } catch (error) {
      throw error
    }
  }

  // Publish artwork
  async publishArtwork (artworkId: string) {
    try {
      return await this.api.post(`profile/artworks/${artworkId}/publish`)
    } catch (error) {
      throw error
    }
  }

  // Unhide artwork
  async unhideArtwork (artworkId: string) {
    return this.api.post(`profile/artworks/${artworkId}/unhide`)
  }

  // Remove artwork
  async removeArtwork (artworkId: string) {
    try {
      return await this.api.delete(`profile/artworks/${artworkId}`)
    } catch (error) {
      throw error
    }
  }

  // Remove media from artwork
  async removeArtworkImage (artworkId: string, mediaId: string) {
    try {
      return await this.api.delete(`profile/artworks/${artworkId}/media/${mediaId}`)
    } catch (error) {
      throw error
    }
  }

  // Clone artwork
  async cloneArtwork (artworkId: string) {
    try {
      const res = await this.api.post(`profile/artworks/${artworkId}/clone`, {})
      if (res.data['data']) {
        return new ProfileArtworkTransformer().toClass(res.data['data'])
      }
    } catch (error) {
      throw error
    }
  }

  async addReaction (artwork: Artwork, reaction: ArtworkReaction): Promise<void> {
    await this.api.post(`artworks/${artwork.id}/reactions/${reaction}`)
  }

}
